import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { X, Users, Trash2, Search, UserPlus, Download, Cake, Phone, ArrowLeft, MapPin, Move, Save, RotateCcw, Edit3, Plus, Minus, Hash, AlertTriangle, UserCheck, Printer } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Table layout matching the actual floor plan (1536×1024) ─────────────────
const TW = 26, GAP = 12, STEP = TW + GAP; // 38px step

// Individual table positions mapped from the planimetria image
const ALL_TABLES = [
  // === TOP SECTION (above lights, y ≈ 100-210) ===
  // Row 1 - left group (4)
  {number:1, x:374, y:100}, {number:2, x:412, y:100}, {number:3, x:450, y:100}, {number:4, x:488, y:100},
  // Row 1 - center group (4)
  {number:5, x:534, y:100}, {number:6, x:572, y:100}, {number:7, x:610, y:100}, {number:8, x:648, y:100},
  // Row 1 - right group (5)
  {number:9, x:858, y:100}, {number:10, x:896, y:100}, {number:11, x:934, y:100}, {number:12, x:972, y:100}, {number:13, x:1010, y:100},
  // Row 2 - left (6, wider)
  {number:14, x:298, y:148}, {number:15, x:336, y:148}, {number:16, x:374, y:148}, {number:17, x:412, y:148}, {number:18, x:450, y:148}, {number:19, x:488, y:148},
  // Row 2 - center (4)
  {number:20, x:534, y:148}, {number:21, x:572, y:148}, {number:22, x:610, y:148}, {number:23, x:648, y:148},
  // Row 2 - right (6, wider)
  {number:24, x:820, y:148}, {number:25, x:858, y:148}, {number:26, x:896, y:148}, {number:27, x:934, y:148}, {number:28, x:972, y:148}, {number:29, x:1010, y:148},
  // Row 3 - left (3)
  {number:30, x:336, y:196}, {number:31, x:374, y:196}, {number:32, x:412, y:196},
  // Row 3 - center (4)
  {number:33, x:534, y:196}, {number:34, x:572, y:196}, {number:35, x:610, y:196}, {number:36, x:648, y:196},
  // Row 3 - right (4)
  {number:37, x:896, y:192}, {number:38, x:934, y:192}, {number:39, x:972, y:192}, {number:40, x:1010, y:192},

  // === RIGHT SIDE - top strip (y ≈ 232) ===
  {number:41, x:1146, y:232}, {number:42, x:1184, y:232}, {number:43, x:1222, y:232}, {number:44, x:1260, y:232}, {number:45, x:1298, y:232},

  // === RIGHT WALL - upper block (2r × 6c) ===
  {number:46, x:1046, y:355}, {number:47, x:1084, y:355}, {number:48, x:1122, y:355}, {number:49, x:1160, y:355}, {number:50, x:1198, y:355}, {number:51, x:1236, y:355},
  {number:52, x:1046, y:400}, {number:53, x:1084, y:400}, {number:54, x:1122, y:400}, {number:55, x:1160, y:400}, {number:56, x:1198, y:400}, {number:57, x:1236, y:400},

  // === RIGHT WALL - lower block (3r × 5c) ===
  {number:58, x:1090, y:448}, {number:59, x:1128, y:448}, {number:60, x:1166, y:448}, {number:61, x:1204, y:448}, {number:62, x:1242, y:448},
  {number:63, x:1090, y:490}, {number:64, x:1128, y:490}, {number:65, x:1166, y:490}, {number:66, x:1204, y:490}, {number:67, x:1242, y:490},
  {number:68, x:1090, y:534}, {number:69, x:1128, y:534}, {number:70, x:1166, y:534}, {number:71, x:1204, y:534}, {number:72, x:1242, y:534},

  // === CENTER-LEFT CLUSTER (below lights, 3r × 4c) ===
  {number:73, x:567, y:261}, {number:74, x:643, y:261}, {number:75, x:680, y:261},
  {number:76, x:567, y:307}, {number:77, x:605, y:307}, {number:78, x:643, y:307}, {number:79, x:680, y:307},
  {number:80, x:569, y:350}, {number:81, x:607, y:350}, {number:82, x:645, y:350}, {number:83, x:681, y:350},
  {number:84, x:268, y:418}, {number:85, x:306, y:418}, {number:86, x:344, y:418}, {number:87, x:382, y:418}, {number:88, x:420, y:418},

  // === LEFT WALL - lower block (4r × 5c) ===
  {number:89, x:322, y:448}, {number:90, x:360, y:448}, {number:91, x:398, y:448}, {number:92, x:436, y:448}, {number:93, x:474, y:448},
  {number:94, x:322, y:490}, {number:95, x:360, y:490}, {number:96, x:398, y:490}, {number:97, x:436, y:490}, {number:98, x:474, y:490},
  {number:99, x:322, y:534}, {number:100, x:360, y:534}, {number:101, x:398, y:534}, {number:102, x:436, y:534}, {number:103, x:474, y:534},
  {number:104, x:322, y:576}, {number:105, x:360, y:576}, {number:106, x:398, y:576}, {number:107, x:436, y:576}, {number:108, x:474, y:576},

  // === CENTER - left group (3r × 2c) ===
  {number:109, x:540, y:602}, {number:110, x:578, y:602},
  {number:111, x:540, y:640}, {number:112, x:578, y:640},
  {number:113, x:540, y:678}, {number:114, x:578, y:678},

  // === CENTER - right group (3r × 3c) ===
  {number:115, x:948, y:616}, {number:116, x:986, y:616}, {number:117, x:1024, y:616},
  {number:118, x:948, y:654}, {number:119, x:986, y:654}, {number:120, x:1024, y:654},
  {number:121, x:948, y:692}, {number:122, x:986, y:692}, {number:123, x:1024, y:692},

  // === BOTTOM-RIGHT - upper row ===
  {number:124, x:1110, y:632}, {number:125, x:1148, y:632}, {number:126, x:1186, y:632}, {number:127, x:1224, y:632},

  // === BOTTOM-RIGHT - left column (3r × 3c) ===
  {number:128, x:966, y:736}, {number:129, x:1004, y:736}, {number:130, x:1042, y:736},
  {number:131, x:966, y:778}, {number:132, x:1042, y:778},
  {number:133, x:966, y:818}, {number:134, x:1042, y:818},

  // === BOTTOM-RIGHT - far column (3r × 3c) ===
  {number:135, x:1166, y:748}, {number:136, x:1204, y:748}, {number:137, x:1242, y:748},
  {number:138, x:1166, y:790}, {number:139, x:1204, y:790}, {number:140, x:1242, y:790},
  // === CENTER-RIGHT CLUSTER (below lights, 3r × 3c) ===
  {number:141, x:841, y:260}, {number:142, x:879, y:260}, {number:143, x:917, y:260},

  // === TABLE 144 + CENTER-RIGHT continued ===
  {number:144, x:605, y:261}, {number:145, x:841, y:297}, {number:146, x:879, y:297}, {number:147, x:917, y:297}, {number:148, x:842, y:341}, {number:149, x:880, y:341}, {number:150, x:917, y:341},
];

// ── Table Detail Modal ──────────────────────────────────────────────────────
function TableDetailModal({ tableNumber, data, eventId, token, onClose, onRefresh }) {
  const [clientName, setClientName] = useState('');
  const [clientLastName, setClientLastName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientPeople, setClientPeople] = useState(1);
  const [clientNotes, setClientNotes] = useState('');
  const [hasCake, setHasCake] = useState(false);
  const [cakeText, setCakeText] = useState('');
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const bookings = data?.bookings || [];
  const manualClients = data?.manual_clients || [];
  const totalPeople = bookings.reduce((s, b) => s + (b.num_people || 0), 0)
    + manualClients.reduce((s, c) => s + (c.num_people || 0), 0);
  const isOccupied = bookings.length > 0 || manualClients.length > 0;

  const handleAddClient = async (e) => {
    e.preventDefault();
    if (!clientName.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/tables/${eventId}/${tableNumber}/clients`, {
        client_name: clientName.trim(),
        last_name: clientLastName.trim(),
        phone: clientPhone.trim(),
        num_people: clientPeople,
        notes: clientNotes.trim(),
        has_cake: hasCake,
        cake_text: cakeText.trim(),
      }, { headers });
      setClientName(''); setClientLastName(''); setClientPhone('');
      setClientPeople(1); setClientNotes('');
      setHasCake(false); setCakeText('');
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    } finally { setSaving(false); }
  };

  const handleRemoveClient = async (clientId) => {
    if (!window.confirm('Rimuovere questo cliente dal tavolo?')) return;
    try {
      await axios.delete(`${API}/admin/tables/${eventId}/${tableNumber}/clients/${clientId}`, { headers });
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleUnassignBooking = async (bookingId) => {
    if (!window.confirm('Liberare questo tavolo dalla prenotazione? La prenotazione tornerà in attesa di assegnazione.')) return;
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/table`, { table_number: null }, { headers });
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="min-h-full flex items-center justify-center">
        <div className="w-full max-w-lg rounded-2xl p-6" style={{ background: '#111120', border: '1px solid rgba(255,255,255,0.1)' }}>
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center font-playfair text-xl font-bold"
                style={{
                  background: isOccupied ? 'rgba(239,68,68,0.15)' : 'rgba(59,130,246,0.15)',
                  border: `2px solid ${isOccupied ? '#ef4444' : '#3b82f6'}`,
                  color: isOccupied ? '#ef4444' : '#3b82f6',
                }}
              >
                {tableNumber}
              </div>
              <div>
                <h2 className="font-playfair text-xl font-bold text-white">Tavolo {tableNumber}</h2>
                <span
                  className="inline-block text-xs font-manrope font-600 px-2 py-0.5 rounded-full mt-1"
                  style={{
                    background: isOccupied ? 'rgba(239,68,68,0.15)' : 'rgba(59,130,246,0.15)',
                    color: isOccupied ? '#ef4444' : '#3b82f6',
                  }}
                >
                  {isOccupied ? 'OCCUPATO' : 'LIBERO'}
                </span>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors" data-testid="close-table-modal">
              <X size={22} />
            </button>
          </div>

          {/* People count */}
          {isOccupied && (
            <div className="flex items-center gap-2 mb-5 px-4 py-3 rounded-xl" style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.15)' }}>
              <Users size={16} style={{ color: 'var(--color-gold)' }} />
              <span className="text-gold font-manrope font-600 text-sm">{totalPeople} {totalPeople === 1 ? 'persona' : 'persone'} al tavolo</span>
            </div>
          )}

          {/* Bookings */}
          {bookings.length > 0 && (
            <div className="mb-5">
              <h3 className="text-gray-400 text-xs font-manrope uppercase tracking-wider mb-3">Prenotazioni associate</h3>
              <div className="space-y-2">
                {bookings.map(b => (
                  <div key={b.id} className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-manrope font-600 text-sm">{b.first_name} {b.last_name}</p>
                        <p className="text-gray-500 text-xs mt-0.5">{b.booking_code} &bull; {b.num_people} pers. &bull; {b.package_label}</p>
                        {b.phone && <p className="text-gray-500 text-xs mt-0.5">Tel: {b.phone}</p>}
                        {b.extras?.bottiglia && (
                          <p className="text-xs mt-1 flex items-center gap-1" style={{ color: '#a78bfa' }}>
                            <span>🍾</span> Bottiglia: {b.extras.bottiglia_nome || 'Si'}
                          </p>
                        )}
                        {b.extras?.torta && (
                          <p className="text-xs mt-1 flex items-center gap-1" style={{ color: '#f59e0b' }}>
                            <Cake size={12} /> Torta{b.extras.torta_testo ? `: "${b.extras.torta_testo}"` : ''}
                          </p>
                        )}
                        {b.extras?.apericena && (
                          <p className="text-xs mt-1" style={{ color: '#c9a84c' }}>
                            Apericena inclusa
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleUnassignBooking(b.id)}
                        className="text-gray-600 hover:text-red-400 transition-colors p-1 shrink-0"
                        title="Libera tavolo"
                        data-testid={`unassign-booking-${b.id}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Manual clients */}
          <div className="mb-5">
            <h3 className="text-gray-400 text-xs font-manrope uppercase tracking-wider mb-3">Clienti manuali</h3>
            {manualClients.length === 0 ? (
              <p className="text-gray-600 text-sm font-manrope">Nessun cliente manuale assegnato.</p>
            ) : (
              <div className="space-y-2">
                {manualClients.map(c => (
                  <div key={c.id} className="p-3 rounded-lg flex items-start justify-between gap-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-manrope font-600 text-sm">{c.client_name} {c.last_name || ''}</p>
                      <p className="text-gray-500 text-xs mt-0.5">
                        {c.num_people} pers.
                        {c.phone ? <> &bull; <Phone size={10} className="inline mb-0.5" /> {c.phone}</> : ''}
                      </p>
                      {c.has_cake && (
                        <p className="text-xs mt-1 flex items-center gap-1" style={{ color: '#f59e0b' }}>
                          <Cake size={12} /> Torta{c.cake_text ? `: "${c.cake_text}"` : ''}
                        </p>
                      )}
                      {c.notes && <p className="text-gray-600 text-xs mt-0.5 italic">{c.notes}</p>}
                    </div>
                    <button
                      onClick={() => handleRemoveClient(c.id)}
                      className="text-gray-600 hover:text-red-400 transition-colors p-1 shrink-0"
                      data-testid={`remove-manual-client-${c.id}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add manual client form */}
          <div className="border-t border-white/10 pt-5">
            <h3 className="text-gray-400 text-xs font-manrope uppercase tracking-wider mb-3">Aggiungi cliente manuale</h3>
            <form onSubmit={handleAddClient} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Nome *"
                  value={clientName}
                  onChange={e => setClientName(e.target.value)}
                  className="input-elegant"
                  data-testid="manual-client-name"
                />
                <input
                  type="text"
                  placeholder="Cognome"
                  value={clientLastName}
                  onChange={e => setClientLastName(e.target.value)}
                  className="input-elegant"
                  data-testid="manual-client-lastname"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-gray-500 text-xs mb-1 block font-manrope">Telefono</label>
                  <input
                    type="tel"
                    placeholder="333 1234567"
                    value={clientPhone}
                    onChange={e => setClientPhone(e.target.value)}
                    className="input-elegant"
                    data-testid="manual-client-phone"
                  />
                </div>
                <div>
                  <label className="text-gray-500 text-xs mb-1 block font-manrope">N. persone</label>
                  <input
                    type="number" min="1" max="20"
                    value={clientPeople}
                    onChange={e => setClientPeople(parseInt(e.target.value) || 1)}
                    className="input-elegant"
                    data-testid="manual-client-people"
                  />
                </div>
              </div>
              {/* Cake toggle */}
              <div className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasCake}
                    onChange={e => { setHasCake(e.target.checked); if (!e.target.checked) setCakeText(''); }}
                    className="w-5 h-5"
                    style={{ accentColor: '#f59e0b' }}
                    data-testid="manual-client-cake-toggle"
                  />
                  <Cake size={16} style={{ color: hasCake ? '#f59e0b' : '#6b7280' }} />
                  <span className="font-manrope text-sm" style={{ color: hasCake ? '#f59e0b' : '#9ca3af' }}>
                    Torta
                  </span>
                </label>
                {hasCake && (
                  <input
                    type="text"
                    placeholder="Testo torta (es. Buon Compleanno Marco!)"
                    value={cakeText}
                    onChange={e => setCakeText(e.target.value)}
                    className="input-elegant mt-2"
                    data-testid="manual-client-cake-text"
                  />
                )}
              </div>
              <input
                type="text"
                placeholder="Note (opzionale)"
                value={clientNotes}
                onChange={e => setClientNotes(e.target.value)}
                className="input-elegant"
                data-testid="manual-client-notes"
              />
              <button
                type="submit"
                disabled={!clientName.trim() || saving}
                className="btn-primary w-full justify-center"
                data-testid="add-manual-client-btn"
              >
                {saving ? 'Salvataggio...' : <><UserPlus size={16} /> Aggiungi al tavolo</>}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Table Map Component ────────────────────────────────────────────────
export default function TableMap({ events, token }) {
  const [selectedEventId, setSelectedEventId] = useState('');
  const [tableData, setTableData] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [hoveredTable, setHoveredTable] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(false);

  // Dynamic table list (loaded from DB or defaults)
  const [tables, setTables] = useState(ALL_TABLES);
  const [editMode, setEditMode] = useState(false);
  const [editTables, setEditTables] = useState(null);
  const [editTool, setEditTool] = useState('move'); // 'move' | 'add' | 'remove'
  const [dragInfo, setDragInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [renamingTable, setRenamingTable] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [pendingBookings, setPendingBookings] = useState([]);
  const [showManualBooking, setShowManualBooking] = useState(false);
  const [manualForm, setManualForm] = useState({ first_name: '', last_name: '', phone: '', num_people: 1, package_type: 'ingresso_tavolo_bottiglia', package_label: 'Ingresso + Tavolo e Bottiglia', notes: '' });
  const [assigningBooking, setAssigningBooking] = useState(null); // booking being assigned to a table
  const mapRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };
  const activeTables = editMode && editTables ? editTables : tables;

  // Load table layout from backend
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/table-layout`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (res.data.tables && res.data.tables.length > 0) setTables(res.data.tables); })
      .catch(() => {});
  }, [token]);

  // ── Edit mode helpers ─────────────────────────────────────────────────────
  const enterEditMode = () => {
    setEditTables(tables.map(t => ({ ...t })));
    setEditMode(true);
    setEditTool('move');
    setHasUnsavedChanges(false);
    setRenamingTable(null);
  };

  const saveLayout = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/table-layout`, { tables: editTables }, { headers });
      setTables(editTables);
      setEditMode(false);
      setEditTables(null);
      setHasUnsavedChanges(false);
      setEditTool('move');
      setRenamingTable(null);
    } catch (err) { alert('Errore nel salvataggio'); }
    finally { setSaving(false); }
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditTables(null);
    setHasUnsavedChanges(false);
    setEditTool('move');
    setDragInfo(null);
    setRenamingTable(null);
  };

  const resetLayout = async () => {
    if (!window.confirm('Ripristinare il layout predefinito (150 tavoli originali)?')) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/table-layout`, { tables: [] }, { headers });
      setTables(ALL_TABLES);
      setEditMode(false);
      setEditTables(null);
      setHasUnsavedChanges(false);
    } catch (err) { alert('Errore nel ripristino'); }
    finally { setSaving(false); }
  };

  // ── Drag handlers ─────────────────────────────────────────────────────────
  const handleDragStart = (e, tableNumber) => {
    if (!editMode || editTool !== 'move') return;
    e.preventDefault();
    e.stopPropagation();
    const rect = mapRef.current.getBoundingClientRect();
    const t = editTables.find(t => t.number === tableNumber);
    if (!t) return;
    setDragInfo({ tableNumber, startMouseX: e.clientX, startMouseY: e.clientY, origX: t.x, origY: t.y, mapW: rect.width, mapH: rect.height });
  };

  const handleMouseMove = useCallback((e) => {
    if (!dragInfo || !editTables) return;
    const dx = e.clientX - dragInfo.startMouseX;
    const dy = e.clientY - dragInfo.startMouseY;
    const newX = Math.round(dragInfo.origX + (dx / dragInfo.mapW) * 1536);
    const newY = Math.round(dragInfo.origY + (dy / dragInfo.mapH) * 1024);
    setEditTables(prev => prev.map(t =>
      t.number === dragInfo.tableNumber
        ? { ...t, x: Math.max(0, Math.min(1510, newX)), y: Math.max(0, Math.min(998, newY)) }
        : t
    ));
    setHasUnsavedChanges(true);
  }, [dragInfo]);

  const handleMouseUp = useCallback(() => { setDragInfo(null); }, []);

  useEffect(() => {
    if (!dragInfo) return;
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
  }, [dragInfo, handleMouseMove, handleMouseUp]);

  // ── Add table (click on map in 'add' mode) ───────────────────────────────
  const handleMapClick = (e) => {
    if (!editMode || editTool !== 'add' || !mapRef.current) return;
    const rect = mapRef.current.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 1536) - Math.round(TW / 2);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 1024) - Math.round(TW / 2);
    const maxNum = editTables.reduce((max, t) => Math.max(max, t.number), 0);
    setEditTables([...editTables, { number: maxNum + 1, x: Math.max(0, Math.min(1510, x)), y: Math.max(0, Math.min(998, y)) }]);
    setHasUnsavedChanges(true);
  };

  // ── Remove table (click on table in 'remove' mode) ───────────────────────
  const handleTableClickInEdit = (tableNum) => {
    if (editTool === 'remove') {
      if (window.confirm(`Rimuovere il tavolo ${tableNum}?`)) {
        setEditTables(editTables.filter(t => t.number !== tableNum));
        setHasUnsavedChanges(true);
      }
    }
  };

  // ── Rename table (double-click) ───────────────────────────────────────────
  const handleTableDoubleClick = (e, tableNum) => {
    if (!editMode) return;
    e.preventDefault();
    e.stopPropagation();
    setRenamingTable(tableNum);
    setRenameValue(String(tableNum));
  };

  const handleRenameSubmit = () => {
    const newNum = parseInt(renameValue);
    if (isNaN(newNum) || newNum < 1) { setRenamingTable(null); return; }
    if (newNum === renamingTable) { setRenamingTable(null); return; }
    // If the new number already exists, swap the two tables
    const existingTable = editTables.find(t => t.number === newNum);
    if (existingTable) {
      setEditTables(editTables.map(t => {
        if (t.number === renamingTable) return { ...t, number: newNum };
        if (t.number === newNum) return { ...t, number: renamingTable };
        return t;
      }));
    } else {
      setEditTables(editTables.map(t => t.number === renamingTable ? { ...t, number: newNum } : t));
    }
    setRenamingTable(null);
    setHasUnsavedChanges(true);
  };

  const loadTableData = useCallback(async () => {
    if (!selectedEventId) { setTableData([]); setPendingBookings([]); return; }
    setLoading(true);
    try {
      const [tablesRes, pendingRes] = await Promise.all([
        axios.get(`${API}/admin/tables/${selectedEventId}`, { headers }),
        axios.get(`${API}/admin/tables/${selectedEventId}/pending-bookings`, { headers }),
      ]);
      setTableData(tablesRes.data);
      setPendingBookings(pendingRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [selectedEventId, token]);

  useEffect(() => { loadTableData(); }, [loadTableData]);

  // Build occupied map
  const occupiedMap = {};
  for (const t of tableData) {
    occupiedMap[t.table_number] = t;
  }

  const isOccupied = (num) => !!occupiedMap[num];

  const totalCount = activeTables.length;
  const freeCount = activeTables.filter(t => !isOccupied(t.number)).length;
  const occupiedCount = totalCount - freeCount;

  // Filtered tables for search/filter
  const getTableOpacity = (tableNum) => {
    const matchesSearch = searchQuery === '' || String(tableNum).includes(searchQuery);
    const occupied = isOccupied(tableNum);
    const matchesFilter = filterStatus === 'all'
      || (filterStatus === 'free' && !occupied)
      || (filterStatus === 'occupied' && occupied);
    return matchesSearch && matchesFilter ? 1 : 0.15;
  };

  const handleTableClick = (tableNum) => {
    if (editMode) {
      handleTableClickInEdit(tableNum);
      return;
    }
    if (assigningBooking) {
      if (isOccupied(tableNum)) {
        alert(`Il tavolo ${tableNum} e' gia' occupato`);
        return;
      }
      assignBookingToTable(assigningBooking.id, tableNum);
      return;
    }
    if (!selectedEventId) return;
    setSelectedTable(tableNum);
  };

  // Assign a pending booking to a table
  const assignBookingToTable = async (bookingId, tableNumber) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/table`, { table_number: tableNumber }, { headers });
      setAssigningBooking(null);
      loadTableData();
    } catch (err) { alert('Errore nell\'assegnazione'); }
  };

  // Create a manual booking
  const createManualBooking = async () => {
    if (!manualForm.first_name.trim() || !manualForm.last_name.trim()) return;
    try {
      await axios.post(`${API}/admin/bookings/manual`, { ...manualForm, event_id: selectedEventId }, { headers });
      setShowManualBooking(false);
      setManualForm({ first_name: '', last_name: '', phone: '', num_people: 1, package_type: 'ingresso_tavolo_bottiglia', package_label: 'Ingresso + Tavolo e Bottiglia', notes: '' });
      loadTableData();
    } catch (err) { alert('Errore nella creazione'); }
  };

  const selectedEvent = events.find(e => e.id === selectedEventId);

  const exportPDF = () => {
    if (!selectedEvent) return;
    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();

    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('La Peschiera Club', pageW / 2, 18, { align: 'center' });
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Serata: ${selectedEvent.title}  —  ${selectedEvent.date}`, pageW / 2, 26, { align: 'center' });

    // Stats
    doc.setFontSize(10);
    doc.text(`Tavoli occupati: ${occupiedCount} / ${totalCount}  |  Totale persone: ${tableData.reduce((s, t) => {
      const bp = (t.bookings || []).reduce((a, b) => a + (b.num_people || 0), 0);
      const mp = (t.manual_clients || []).reduce((a, c) => a + (c.num_people || 0), 0);
      return s + bp + mp;
    }, 0)}`, pageW / 2, 34, { align: 'center' });

    // Build rows: sort occupied tables by number
    const rows = [];
    const sorted = [...tableData].sort((a, b) => a.table_number - b.table_number);
    for (const t of sorted) {
      const allClients = [];
      for (const b of (t.bookings || [])) {
        const extras = [];
        if (b.extras?.torta) extras.push(b.extras.torta_testo ? `Torta: "${b.extras.torta_testo}"` : 'Torta: Si');
        if (b.extras?.bottiglia) extras.push(b.extras.bottiglia_nome ? `Bott: ${b.extras.bottiglia_nome}` : 'Bottiglia: Si');
        if (b.extras?.apericena) extras.push('Apericena');
        allClients.push({
          name: `${b.first_name} ${b.last_name}`,
          people: b.num_people,
          phone: b.phone || '',
          pkg: b.package_label || '',
          extras: extras.join(' | '),
        });
      }
      for (const c of (t.manual_clients || [])) {
        const extras = [];
        if (c.has_cake) extras.push(c.cake_text ? `Torta: "${c.cake_text}"` : 'Torta: Si');
        allClients.push({
          name: `${c.client_name} ${c.last_name || ''}`.trim(),
          people: c.num_people,
          phone: c.phone || '',
          pkg: 'Manuale',
          extras: extras.join(' | '),
        });
      }
      const totalP = allClients.reduce((s, c) => s + c.people, 0);
      for (let i = 0; i < allClients.length; i++) {
        const c = allClients[i];
        rows.push([
          i === 0 ? String(t.table_number) : '',
          c.name,
          String(c.people),
          c.phone,
          c.pkg,
          c.extras,
          i === 0 ? String(totalP) : '',
        ]);
      }
    }

    autoTable(doc, {
      startY: 40,
      head: [['Tav.', 'Referente', 'Pers.', 'Telefono', 'Pacchetto', 'Extra (Torta/Bottiglia)', 'Tot.']],
      body: rows.length > 0 ? rows : [['—', 'Nessun tavolo occupato', '', '', '', '', '']],
      styles: { fontSize: 7.5, cellPadding: 2 },
      headStyles: { fillColor: [30, 30, 50], textColor: [201, 168, 76] },
      columnStyles: {
        0: { cellWidth: 12, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 12, halign: 'center' },
        5: { cellWidth: 42 },
        6: { cellWidth: 12, halign: 'center', fontStyle: 'bold' },
      },
      alternateRowStyles: { fillColor: [245, 245, 250] },
    });

    doc.save(`tavoli_${selectedEvent.title.replace(/\s+/g, '_')}_${selectedEvent.date}.pdf`);
  };

  const printableMapRef = useRef(null);

  const printMap = async () => {
    if (!selectedEvent) return;
    const activeTbls = editMode ? editTables.filter(t => t.active !== false) : tables.filter(t => t.active !== false);
    const positions = ALL_TABLES.reduce((acc, t) => { acc[t.number] = { x: t.x, y: t.y }; return acc; }, {});

    // Create a temporary off-screen container for high-res rendering
    const container = document.createElement('div');
    container.style.cssText = 'position:fixed;left:-9999px;top:0;width:1536px;height:1024px;background:#111;overflow:hidden;';
    document.body.appendChild(container);

    // Background image
    const img = document.createElement('img');
    img.src = '/planimetria.jpg';
    img.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:fill;opacity:0.4;';
    container.appendChild(img);

    // Wait for image
    await new Promise(r => { img.onload = r; setTimeout(r, 2000); });

    // Draw tables
    for (const t of activeTbls) {
      const pos = positions[t.number];
      if (!pos) continue;
      const occ = occupiedMap[t.number];
      const isOcc = occ && ((occ.bookings?.length > 0) || (occ.manual_clients?.length > 0));

      const dot = document.createElement('div');
      dot.style.cssText = `
        position:absolute;
        left:${pos.x}%;top:${pos.y}%;
        width:28px;height:28px;
        transform:translate(-50%,-50%);
        border-radius:50%;
        display:flex;align-items:center;justify-content:center;
        background:${isOcc ? 'rgba(220,50,50,0.9)' : 'rgba(34,197,94,0.85)'};
        border:2px solid ${isOcc ? '#ff6b6b' : '#4ade80'};
        box-shadow:0 0 6px ${isOcc ? 'rgba(220,50,50,0.5)' : 'rgba(34,197,94,0.4)'};
      `;
      const num = document.createElement('span');
      num.textContent = t.number;
      num.style.cssText = 'color:#fff;font-size:11px;font-weight:800;font-family:Arial,sans-serif;line-height:1;';
      dot.appendChild(num);
      container.appendChild(dot);
    }

    // Title overlay
    const title = document.createElement('div');
    title.style.cssText = 'position:absolute;top:8px;left:50%;transform:translateX(-50%);text-align:center;z-index:10;';
    title.innerHTML = `<div style="background:rgba(0,0,0,0.75);padding:8px 24px;border-radius:12px;border:1px solid rgba(201,168,76,0.3);">
      <div style="color:#c9a84c;font-size:16px;font-weight:bold;font-family:Arial;">La Peschiera Club</div>
      <div style="color:#fff;font-size:13px;font-family:Arial;">${selectedEvent.title} — ${selectedEvent.date}</div>
    </div>`;
    container.appendChild(title);

    // Legend
    const legend = document.createElement('div');
    legend.style.cssText = 'position:absolute;bottom:8px;right:12px;z-index:10;background:rgba(0,0,0,0.75);padding:6px 14px;border-radius:8px;display:flex;gap:16px;align-items:center;';
    legend.innerHTML = `
      <span style="display:flex;align-items:center;gap:4px;"><span style="width:12px;height:12px;border-radius:50%;background:#22c55e;display:inline-block;"></span><span style="color:#ccc;font-size:11px;font-family:Arial;">Libero</span></span>
      <span style="display:flex;align-items:center;gap:4px;"><span style="width:12px;height:12px;border-radius:50%;background:#dc3232;display:inline-block;"></span><span style="color:#ccc;font-size:11px;font-family:Arial;">Occupato</span></span>
    `;
    container.appendChild(legend);

    try {
      const canvas = await html2canvas(container, { scale: 2, useCORS: true, backgroundColor: '#111' });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pW = doc.internal.pageSize.getWidth();
      const pH = doc.internal.pageSize.getHeight();
      doc.addImage(imgData, 'JPEG', 0, 0, pW, pH);
      doc.save(`Mappa_${selectedEvent.title.replace(/\s+/g, '_')}_${selectedEvent.date}.pdf`);
    } catch (err) {
      alert('Errore nella generazione della mappa: ' + err.message);
    } finally {
      document.body.removeChild(container);
    }
  };

  return (
    <div data-testid="table-map-section">
      {!selectedEventId ? (
        /* ── Event Selection Dashboard ── */
        <div>
          <h1 className="font-playfair text-3xl font-bold text-white mb-6">Mappa Tavoli</h1>
          <p className="text-gray-400 font-manrope mb-6">Seleziona una serata per gestire i tavoli</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map(ev => (
              <button
                key={ev.id}
                onClick={() => { setSelectedEventId(ev.id); setSelectedTable(null); }}
                className="card-dark p-5 text-left hover:border-gold transition-all group"
                style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                data-testid={`event-card-${ev.id}`}
              >
                {ev.image_url && (
                  <div className="w-full h-32 rounded-lg overflow-hidden mb-3">
                    <img
                      src={ev.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${ev.image_url}` : ev.image_url}
                      alt={ev.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={e => { e.target.style.objectFit='contain'; e.target.style.padding='1rem'; e.target.style.background='#0a0a0f'; e.target.src="/api/uploads/logo_fallback.png"; }}
                    />
                  </div>
                )}
                <h3 className="font-playfair text-lg font-bold text-white group-hover:text-gold transition-colors">{ev.title}</h3>
                <p className="text-gold font-manrope text-sm mt-1">{ev.date} &bull; ore {ev.time}</p>
                {ev.description && <p className="text-gray-500 text-sm mt-1 line-clamp-2">{ev.description}</p>}
                <div className="mt-3 flex items-center gap-2 text-gray-400 group-hover:text-gold transition-colors">
                  <MapPin size={14} />
                  <span className="text-xs font-manrope font-600 uppercase tracking-wider">Gestisci tavoli</span>
                </div>
              </button>
            ))}
          </div>
          {events.length === 0 && (
            <div className="card-dark p-12 text-center">
              <p className="text-gray-500 font-manrope">Nessun evento attivo. Crea un evento dalla sezione "Gestione eventi".</p>
            </div>
          )}
        </div>
      ) : (
        /* ── Table Map View ── */
        <div>
          {/* Header with back button */}
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => { setSelectedEventId(''); setSelectedTable(null); setTableData([]); }}
                className="w-10 h-10 rounded-lg border border-white/10 flex items-center justify-center text-gray-400 hover:border-gold hover:text-gold transition-all shrink-0"
                data-testid="back-to-events-btn"
              >
                <ArrowLeft size={18} />
              </button>
              <div>
                <h1 className="font-playfair text-2xl font-bold text-white">{selectedEvent?.title}</h1>
                <p className="text-gold font-manrope text-sm">{selectedEvent?.date} &bull; ore {selectedEvent?.time}</p>
              </div>
            </div>
            {occupiedCount > 0 && !editMode && (
              <button
                onClick={exportPDF}
                className="btn-secondary flex items-center gap-2 text-sm py-2.5 px-4"
                data-testid="export-pdf-btn"
              >
                <Download size={16} /> Esporta PDF
              </button>
            )}
            {!editMode && (
              <button
                onClick={printMap}
                className="btn-secondary flex items-center gap-2 text-sm py-2.5 px-4"
                data-testid="print-map-btn"
                style={{ borderColor: 'rgba(201,168,76,0.3)', color: 'var(--color-gold)' }}
              >
                <Printer size={16} /> Stampa Mappa
              </button>
            )}
          </div>

          {/* Edit mode banner */}
          {editMode && (
            <div className="mb-4 p-4 rounded-xl" style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)' }}>
              <div className="flex items-center justify-between gap-4 mb-3">
                <div className="flex items-center gap-3">
                  <Edit3 size={18} style={{ color: '#c9a84c' }} />
                  <div>
                    <p className="text-gold font-manrope font-bold text-sm">Modalita' Modifica Attiva</p>
                    <p className="text-gray-400 font-manrope text-xs">Seleziona uno strumento e modifica il layout</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {hasUnsavedChanges && (
                    <span className="text-xs font-manrope px-2 py-1 rounded-full" style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>
                      Modifiche non salvate
                    </span>
                  )}
                  <button onClick={resetLayout} disabled={saving}
                    className="flex items-center gap-1.5 text-xs font-manrope px-3 py-2 rounded-lg transition-all"
                    style={{ background: 'rgba(255,255,255,0.05)', color: '#9ca3af', border: '1px solid rgba(255,255,255,0.1)' }}
                    data-testid="reset-positions-btn"
                  >
                    <RotateCcw size={13} /> Reset
                  </button>
                  <button onClick={cancelEdit} disabled={saving}
                    className="flex items-center gap-1.5 text-xs font-manrope px-3 py-2 rounded-lg transition-all"
                    style={{ background: 'rgba(255,255,255,0.05)', color: '#9ca3af', border: '1px solid rgba(255,255,255,0.1)' }}
                    data-testid="cancel-edit-btn"
                  >
                    <X size={13} /> Annulla
                  </button>
                  <button onClick={saveLayout} disabled={saving || !hasUnsavedChanges}
                    className="flex items-center gap-1.5 text-xs font-manrope font-bold px-4 py-2 rounded-lg transition-all"
                    style={{ background: hasUnsavedChanges ? '#c9a84c' : 'rgba(201,168,76,0.3)', color: hasUnsavedChanges ? '#000' : '#666' }}
                    data-testid="save-positions-btn"
                  >
                    <Save size={13} /> {saving ? 'Salvataggio...' : 'Salva Layout'}
                  </button>
                </div>
              </div>
              {/* Tool bar */}
              <div className="flex items-center gap-2 pt-2" style={{ borderTop: '1px solid rgba(201,168,76,0.15)' }}>
                <span className="text-gray-500 text-xs font-manrope mr-2">Strumenti:</span>
                {[
                  { id: 'move', icon: Move, label: 'Sposta', desc: 'Trascina per spostare' },
                  { id: 'add', icon: Plus, label: 'Aggiungi', desc: 'Clicca sulla mappa per aggiungere' },
                  { id: 'remove', icon: Minus, label: 'Rimuovi', desc: 'Clicca su un tavolo per rimuoverlo' },
                ].map(tool => (
                  <button key={tool.id} onClick={() => { setEditTool(tool.id); setRenamingTable(null); }}
                    className="flex items-center gap-1.5 text-xs font-manrope px-3 py-2 rounded-lg transition-all"
                    style={{
                      background: editTool === tool.id ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.03)',
                      color: editTool === tool.id ? '#c9a84c' : '#9ca3af',
                      border: editTool === tool.id ? '1px solid rgba(201,168,76,0.4)' : '1px solid rgba(255,255,255,0.08)',
                    }}
                    data-testid={`tool-${tool.id}-btn`}
                  >
                    <tool.icon size={14} /> {tool.label}
                  </button>
                ))}
                <span className="text-gray-600 text-xs font-manrope ml-2 italic">
                  {editTool === 'move' && 'Trascina i tavoli | Doppio click per rinumerare'}
                  {editTool === 'add' && 'Clicca sulla mappa per posizionare un nuovo tavolo'}
                  {editTool === 'remove' && 'Clicca su un tavolo per eliminarlo'}
                </span>
                <span className="ml-auto text-gray-500 text-xs font-manrope">
                  Tavoli: {activeTables.length}
                </span>
              </div>
            </div>
          )}
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="card-dark p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.1)' }}>
                <span className="text-gold font-playfair font-bold text-lg">T</span>
              </div>
              <div>
                <p className="text-gray-500 text-xs font-manrope">Totali</p>
                <p className="text-white font-playfair text-2xl font-bold">{totalCount}</p>
              </div>
            </div>
            <div className="card-dark p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(59,130,246,0.1)' }}>
                <div style={{ width: 14, height: 14, background: '#3b82f6', borderRadius: 3 }} />
              </div>
              <div>
                <p className="text-gray-500 text-xs font-manrope">Liberi</p>
                <p className="text-white font-playfair text-2xl font-bold" data-testid="free-tables-count">{freeCount}</p>
              </div>
            </div>
            <div className="card-dark p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.1)' }}>
                <div style={{ width: 14, height: 14, background: '#ef4444', borderRadius: 3 }} />
              </div>
              <div>
                <p className="text-gray-500 text-xs font-manrope">Occupati</p>
                <p className="text-white font-playfair text-2xl font-bold" data-testid="occupied-tables-count">{occupiedCount}</p>
              </div>
            </div>
          </div>

          {/* Assigning mode banner */}
          {assigningBooking && (
            <div className="mb-4 p-4 rounded-xl flex items-center justify-between" style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)' }}>
              <div className="flex items-center gap-3">
                <MapPin size={18} style={{ color: '#3b82f6' }} />
                <div>
                  <p className="text-blue-400 font-manrope font-bold text-sm">Clicca su un tavolo libero per assegnare:</p>
                  <p className="text-white font-manrope text-sm">{assigningBooking.first_name} {assigningBooking.last_name} — {assigningBooking.num_people} persone</p>
                </div>
              </div>
              <button onClick={() => setAssigningBooking(null)} className="text-gray-400 hover:text-white transition-colors" data-testid="cancel-assign-btn">
                <X size={18} />
              </button>
            </div>
          )}

          {/* Pending bookings alert */}
          {pendingBookings.length > 0 && !editMode && !assigningBooking && (
            <div className="mb-4 p-4 rounded-xl" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)' }} data-testid="pending-bookings-alert">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle size={16} style={{ color: '#f59e0b' }} />
                <span className="text-yellow-400 font-manrope font-bold text-sm">{pendingBookings.length} prenotazion{pendingBookings.length === 1 ? 'e' : 'i'} in attesa di assegnazione tavolo</span>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {pendingBookings.map(b => (
                  <div key={b.id} className="flex items-center justify-between p-2.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-gold font-playfair text-xs font-bold shrink-0">{b.booking_code}</span>
                      <span className="text-white font-manrope text-sm truncate">{b.first_name} {b.last_name}</span>
                      <span className="text-gray-500 text-xs shrink-0">{b.num_people} pers.</span>
                    </div>
                    <button
                      onClick={() => setAssigningBooking(b)}
                      className="flex items-center gap-1.5 text-xs font-manrope font-bold px-3 py-1.5 rounded-lg shrink-0 transition-all"
                      style={{ background: 'rgba(59,130,246,0.15)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.25)' }}
                      data-testid={`assign-booking-${b.id}`}
                    >
                      <MapPin size={12} /> Assegna tavolo
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add manual booking button */}
          {!editMode && !assigningBooking && (
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setShowManualBooking(true)}
                className="flex items-center gap-2 text-sm font-manrope px-4 py-2 rounded-lg transition-all"
                style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.25)' }}
                data-testid="add-manual-booking-btn"
              >
                <UserPlus size={15} /> Nuova Prenotazione
              </button>
            </div>
          )}

          {/* Manual booking form */}
          {showManualBooking && (
            <div className="mb-4 p-5 rounded-xl" style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)' }} data-testid="manual-booking-form">
              <div className="flex items-center justify-between mb-4">
                <p className="text-green-400 font-manrope font-bold text-sm flex items-center gap-2"><UserPlus size={16} /> Nuova Prenotazione Manuale</p>
                <button onClick={() => setShowManualBooking(false)} className="text-gray-400 hover:text-white"><X size={16} /></button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                <input placeholder="Nome *" value={manualForm.first_name} onChange={e => setManualForm(f => ({ ...f, first_name: e.target.value }))} className="input-elegant text-sm" data-testid="manual-first-name" />
                <input placeholder="Cognome *" value={manualForm.last_name} onChange={e => setManualForm(f => ({ ...f, last_name: e.target.value }))} className="input-elegant text-sm" data-testid="manual-last-name" />
                <input placeholder="Telefono" value={manualForm.phone} onChange={e => setManualForm(f => ({ ...f, phone: e.target.value }))} className="input-elegant text-sm" data-testid="manual-phone" />
                <select value={manualForm.package_type} onChange={e => { const labels = { ingresso_tavolo_bottiglia: 'Ingresso + Tavolo e Bottiglia', ingresso_tavolo_bottiglia_guardaroba: 'Ingresso + Tavolo, Bottiglia e Guardaroba', ingresso_solo_tavolo: 'Ingresso solo Tavolo', solo_ingresso: 'Solo Ingresso', ingresso_guardaroba: 'Ingresso + Guardaroba' }; setManualForm(f => ({ ...f, package_type: e.target.value, package_label: labels[e.target.value] || e.target.value })); }} className="input-elegant text-sm" data-testid="manual-package">
                  <option value="ingresso_tavolo_bottiglia">Tavolo + Bottiglia</option>
                  <option value="ingresso_tavolo_bottiglia_guardaroba">Tavolo + Bottiglia + Guard.</option>
                  <option value="ingresso_solo_tavolo">Solo Tavolo</option>
                  <option value="solo_ingresso">Solo Ingresso</option>
                  <option value="ingresso_guardaroba">Ingresso + Guardaroba</option>
                </select>
                <input type="number" min="1" max="20" placeholder="N. persone" value={manualForm.num_people} onChange={e => setManualForm(f => ({ ...f, num_people: parseInt(e.target.value) || 1 }))} className="input-elegant text-sm" data-testid="manual-people" />
                <input placeholder="Note" value={manualForm.notes} onChange={e => setManualForm(f => ({ ...f, notes: e.target.value }))} className="input-elegant text-sm" data-testid="manual-notes" />
              </div>
              <button
                onClick={createManualBooking}
                disabled={!manualForm.first_name.trim() || !manualForm.last_name.trim()}
                className="flex items-center gap-2 text-sm font-manrope font-bold px-4 py-2 rounded-lg transition-all"
                style={{ background: manualForm.first_name.trim() && manualForm.last_name.trim() ? '#22c55e' : 'rgba(34,197,94,0.3)', color: manualForm.first_name.trim() && manualForm.last_name.trim() ? '#000' : '#666' }}
                data-testid="submit-manual-booking-btn"
              >
                <UserCheck size={14} /> Crea Prenotazione
              </button>
            </div>
          )}

          {/* Search, filter, and edit toggle */}
          <div className="flex gap-3 mb-4">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                placeholder="Cerca tavolo per numero..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="input-elegant pl-10"
                data-testid="table-search-input"
              />
            </div>
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="input-elegant"
              style={{ width: 'auto', minWidth: '140px' }}
              data-testid="table-filter-status"
            >
              <option value="all">Tutti</option>
              <option value="free">Liberi</option>
              <option value="occupied">Occupati</option>
            </select>
            {!editMode && (
              <button
                onClick={enterEditMode}
                className="flex items-center gap-2 text-sm font-manrope px-4 py-2 rounded-lg transition-all"
                style={{ background: 'rgba(201,168,76,0.1)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.25)' }}
                data-testid="toggle-edit-mode-btn"
              >
                <Edit3 size={15} /> Modifica Layout
              </button>
            )}
          </div>

          {/* Interactive Map */}
          <div
            className="relative rounded-2xl overflow-hidden"
            style={{ background: '#070714', border: '1px solid rgba(255,255,255,0.08)' }}
            data-testid="table-map-container"
          >
            {loading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }}>
                <p className="text-gold font-manrope">Caricamento...</p>
              </div>
            )}
            <div className="relative" style={{ paddingBottom: '66.67%' }} ref={mapRef} data-testid="map-planimetria-container">
              <img
                src="/planimetria.jpg"
                alt="Planimetria La Peschiera Club"
                className="absolute inset-0 w-full h-full object-fill"
                style={{ opacity: editMode ? 0.55 : 0.35, pointerEvents: 'none', transition: 'opacity 0.3s' }}
              />
              {/* Table overlay using HTML divs for reliable click handling */}
              <div className="absolute inset-0"
                onClick={handleMapClick}
                style={{
                  cursor: editMode ? (editTool === 'add' ? 'crosshair' : editTool === 'remove' ? 'not-allowed' : 'default') : 'auto',
                }}
              >
                {activeTables.map(table => {
                  const occupied = isOccupied(table.number);
                  const isHovered = hoveredTable === table.number;
                  const isSelected = selectedTable === table.number;
                  const isDragging = dragInfo?.tableNumber === table.number;
                  const isRenaming = renamingTable === table.number;
                  const opacity = getTableOpacity(table.number);
                  const tableInfo = occupiedMap[table.number];
                  const totalPeople = tableInfo
                    ? (tableInfo.bookings || []).reduce((s, b) => s + (b.num_people || 0), 0)
                      + (tableInfo.manual_clients || []).reduce((s, c) => s + (c.num_people || 0), 0)
                    : 0;
                  // Check for extras (cake, bottles) across bookings and manual clients
                  const hasCakeOnTable = tableInfo && (
                    (tableInfo.bookings || []).some(b => b.extras?.torta) ||
                    (tableInfo.manual_clients || []).some(c => c.has_cake)
                  );
                  const hasBottleOnTable = tableInfo && (tableInfo.bookings || []).some(b => b.extras?.bottiglia);

                  const left = (table.x / 1536) * 100;
                  const top = (table.y / 1024) * 100;
                  const w = (TW / 1536) * 100;
                  const h = (TW / 1024) * 100;

                  const editColor = editTool === 'remove' ? '#ef4444' : isDragging ? '#ffd700' : '#22d3ee';
                  const editCursor = editTool === 'move' ? (isDragging ? 'grabbing' : 'grab') : editTool === 'remove' ? 'pointer' : 'default';

                  return (
                    <div
                      key={table.number}
                      onClick={(e) => { e.stopPropagation(); handleTableClick(table.number); }}
                      onMouseDown={(e) => handleDragStart(e, table.number)}
                      onDoubleClick={(e) => handleTableDoubleClick(e, table.number)}
                      onMouseEnter={() => !dragInfo && setHoveredTable(table.number)}
                      onMouseLeave={() => setHoveredTable(null)}
                      data-testid={`table-${table.number}`}
                      style={{
                        position: 'absolute',
                        left: `${left}%`,
                        top: `${top}%`,
                        width: `${w}%`,
                        height: `${h}%`,
                        backgroundColor: editMode ? editColor : (occupied ? '#ef4444' : '#3b82f6'),
                        opacity: isDragging ? 1 : opacity * (isHovered || isSelected ? 0.95 : 0.7),
                        borderRadius: '3px',
                        cursor: editMode ? editCursor : 'pointer',
                        border: isDragging ? '2px solid #fff' : isSelected ? '2px solid #ffd700' : isHovered ? '1.5px solid #fff' : editMode ? '1px solid rgba(255,255,255,0.3)' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: isDragging ? 'none' : 'opacity 0.15s, border 0.15s',
                        zIndex: isDragging ? 50 : isHovered || isRenaming ? 30 : 1,
                        boxShadow: isDragging ? '0 4px 20px rgba(255,215,0,0.4)' : 'none',
                        userSelect: 'none',
                      }}
                    >
                      <span style={{ color: '#fff', fontSize: '0.55vw', fontWeight: 700, fontFamily: 'Manrope, sans-serif', lineHeight: 1, pointerEvents: 'none' }}>
                        {table.number}
                      </span>
                      {/* Extras indicators (cake/bottle) */}
                      {!editMode && occupied && (hasCakeOnTable || hasBottleOnTable) && (
                        <div style={{
                          position: 'absolute', top: '-60%', left: '50%', transform: 'translateX(-50%)',
                          display: 'flex', gap: '1px', pointerEvents: 'none', fontSize: '0.5vw',
                        }}>
                          {hasCakeOnTable && <span title="Torta">🎂</span>}
                          {hasBottleOnTable && <span title="Bottiglia">🍾</span>}
                        </div>
                      )}
                      {/* Tooltip */}
                      {isHovered && !isDragging && !isRenaming && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '110%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: '#111120',
                            border: '1px solid rgba(255,255,255,0.15)',
                            borderRadius: '6px',
                            padding: '4px 10px',
                            whiteSpace: 'nowrap',
                            zIndex: 20,
                            pointerEvents: 'none',
                          }}
                        >
                          <span style={{ color: '#fff', fontSize: '11px', fontFamily: 'Manrope, sans-serif' }}>
                            {editMode
                              ? (editTool === 'remove' ? `Clicca per rimuovere tav. ${table.number}` : `Tav. ${table.number} — Doppio click per rinumerare`)
                              : `Tav. ${table.number} ${occupied ? `(${totalPeople} pers.)${hasCakeOnTable ? ' 🎂' : ''}${hasBottleOnTable ? ' 🍾' : ''}` : '— Libero'}`
                            }
                          </span>
                        </div>
                      )}
                      {/* Rename input */}
                      {isRenaming && (
                        <div
                          onClick={e => e.stopPropagation()}
                          onMouseDown={e => e.stopPropagation()}
                          style={{
                            position: 'absolute',
                            bottom: '120%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: '#1a1a2e',
                            border: '1px solid rgba(201,168,76,0.4)',
                            borderRadius: '8px',
                            padding: '8px',
                            zIndex: 60,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
                          }}
                        >
                          <Hash size={12} style={{ color: '#c9a84c' }} />
                          <input
                            type="number"
                            value={renameValue}
                            onChange={e => setRenameValue(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleRenameSubmit(); if (e.key === 'Escape') setRenamingTable(null); }}
                            autoFocus
                            min="1"
                            className="text-white font-manrope text-sm"
                            style={{
                              width: '60px',
                              background: 'rgba(255,255,255,0.08)',
                              border: '1px solid rgba(255,255,255,0.15)',
                              borderRadius: '4px',
                              padding: '2px 6px',
                              outline: 'none',
                            }}
                            data-testid="rename-table-input"
                          />
                          <button
                            onClick={handleRenameSubmit}
                            style={{ background: '#c9a84c', color: '#000', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: 'bold' }}
                            data-testid="rename-table-confirm"
                          >OK</button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-6 mt-4 px-2">
            <div className="flex items-center gap-2">
              <div style={{ width: 14, height: 14, background: '#3b82f6', borderRadius: 3, opacity: 0.7 }} />
              <span className="text-gray-400 text-sm font-manrope">Libero</span>
            </div>
            <div className="flex items-center gap-2">
              <div style={{ width: 14, height: 14, background: '#ef4444', borderRadius: 3, opacity: 0.7 }} />
              <span className="text-gray-400 text-sm font-manrope">Occupato</span>
            </div>
            <div className="flex items-center gap-2">
              <div style={{ width: 14, height: 14, border: '2px solid #ffd700', borderRadius: 3 }} />
              <span className="text-gray-400 text-sm font-manrope">Selezionato</span>
            </div>
          </div>
        </div>
      )}

      {/* Table detail modal */}
      {selectedTable !== null && selectedEventId && !editMode && (
        <TableDetailModal
          tableNumber={selectedTable}
          data={occupiedMap[selectedTable]}
          eventId={selectedEventId}
          token={token}
          onClose={() => setSelectedTable(null)}
          onRefresh={loadTableData}
        />
      )}
    </div>
  );
}
